import {
  i18nFunct,
  localizePhoneNumbers,
  sendBeaconEvent,
  MarketingPageElementLocation,
  MarketingPageElementType
} from '@lucid/gatsby-theme-lucid';
import { graphql, useStaticQuery } from 'gatsby';
import kebabCase from 'lodash.kebabcase';
import React, { useContext } from 'react';
import LazyLoad from 'react-lazyload';
import Select from 'react-select';

import { languages } from '../../../data/languages';
import LayoutContext from '../../contexts/layoutContext';
import {
  Drupal_Node,
  Drupal_ParagraphMarketoForm,
  FooterSocialIconsJsonEdge
} from '../../generated/graphql';
import { MenuLink } from '../../types/types';
import Link from '../link/link';
import './footer.scss';
import useMarketoLink from '../../hooks/use-marketo-link';

export const footerMenus = {
  default: 'default-footer',
  homepage: 'footer'
};

interface Switcher {
  label: string;
  value: string;
}

interface Props {
  padFooter?: boolean;
  entityTranslations?: Drupal_Node[];
  menu: MenuLink[];
  showSecurity?: boolean;
  isVisio?: boolean;
}

const Footer = ({ padFooter, entityTranslations, menu, isVisio }: Props) => {
  const { language } = useContext(LayoutContext);

  const { langPrefix, shortLangPrefix, i18nKey } = language;

  const { allFooterSocialIconsJson, i18nData } = useStaticQuery(
    graphql`
      {
        allFooterSocialIconsJson {
          edges {
            node {
              name
              logo
              url
            }
          }
        }
        i18nData: allFile(filter: { sourceInstanceName: { eq: "i18n" } }) {
          edges {
            node {
              name
              childSrcJson {
                mktg_footer_services_contact_sales_phone_number
              }
            }
          }
        }
      }
    `
  );
  const socialIcons = allFooterSocialIconsJson.edges as FooterSocialIconsJsonEdge[];
  const i18n = i18nFunct(i18nData, i18nKey);
  let phoneVisible = '';
  let phoneLink = '';
  const visioQueryParams = '?type=visio';

  const disabledLanguages = ['da', 'sv'];
  const switcherValues = Object.keys(languages)
    .filter(thisLang => !disabledLanguages.includes(thisLang))
    .reduce<Record<string, Switcher>>((obj, thisLangPrefix) => {
      const { shortLangPrefix: thisShortLangPrefix } = languages[
        thisLangPrefix
      ];
      const { entityUrl } =
        (entityTranslations || []).find(
          ({ status, entityLanguage }) =>
            status && entityLanguage!.id === thisLangPrefix
        ) || {};

      obj[thisShortLangPrefix] = {
        value: entityUrl?.path
          ? entityUrl.path
          : `/${thisShortLangPrefix === 'en' ? '' : thisShortLangPrefix}`,
        label: languages[thisLangPrefix].name
      };

      return obj;
    }, {});

  if (shortLangPrefix === 'en') {
    phoneVisible = '(888) 875-1867';
    phoneLink = 'tel:+18888751867';
  } else if (
    shortLangPrefix === 'de' ||
    shortLangPrefix === 'fr' ||
    shortLangPrefix === 'nl'
  ) {
    phoneVisible = '+31 (0) 20-225-1568';
    phoneLink = 'tel:+310202251568';
  } else {
    phoneVisible = '+1 (877) 210-9890';
    phoneLink = 'tel:+18772109890';
  }

  interface CustomOptionsProps {
    data: {
      value: string;
      label: string;
    };
    isSelected: boolean;
  }

  const customOption = ({ data, isSelected }: CustomOptionsProps) => (
    <Link
      to={data.value}
      className={`language-switcher__link ${
        isSelected ? 'language-switcher__link--selected' : ''
      }`}
    >
      {data.label}
    </Link>
  );

  const showLangSelect = !disabledLanguages.includes(shortLangPrefix);
  const getMarketoLink = useMarketoLink(language);

  return (
    <footer className={`bottom-footer ${padFooter ? 'cushion' : ''}`}>
      <div
        className="lucid-container lucid-container--flex
                    padding-t-sm padding-b-sm"
      >
        <div className="footer-nav">
          {menu.map(item => {
            return (
              <ul className="footer-links" key={item.title}>
                <li className="footer-links-heading">{item.title}</li>
                {item.links.map(subitem => {
                  let mktoId: string | null = null;
                  const marketoEntity = subitem.fieldMarketo
                    ? subitem.fieldMarketo.entity
                    : null;
                  if (marketoEntity) {
                    const match = (marketoEntity.entityTranslations! as Drupal_Node[]).find(
                      ({ entityLanguage }) => entityLanguage!.id === langPrefix
                    );

                    if (match) {
                      const marketoForm = match as Drupal_ParagraphMarketoForm;
                      mktoId = marketoForm.fieldMarketoId || null;
                    }
                  }

                  if (mktoId) {
                    const { url, copy } = getMarketoLink(mktoId)!;
                    return (
                      <li className="footer-links__item" key={subitem.title}>
                        <Link
                          to={url}
                          inverse
                          underline="hover"
                          className="footer-links__link"
                          data-km-trigger={`footer-${kebabCase(
                            subitem.fieldKmEventName
                          )}`}
                        >
                          {subitem.title || copy}
                        </Link>
                      </li>
                    );
                  }

                  const href =
                    isVisio && subitem.fieldKey === 'pricing'
                      ? `${subitem.link.url!.path}${visioQueryParams}`
                      : subitem.link.url!.path;

                  const beaconEvent = subitem.fieldBeaconElementDetail
                    ? {
                        name: 'clickedMarketingPageElement',
                        elementLocation: MarketingPageElementLocation.footer,
                        elementDetail: subitem.fieldBeaconElementDetail,
                        elementType: MarketingPageElementType.link
                      }
                    : null;

                  return (
                    <li className="footer-links__item" key={subitem.title}>
                      <Link
                        inverse
                        underline="hover"
                        className="footer-links__link"
                        to={href}
                        data-km-trigger={`footer-${kebabCase(
                          subitem.fieldKmEventName
                        )}`}
                        beaconEvent={beaconEvent}
                        onClick={() => subitem.fieldBeaconElementDetail}
                      >
                        {subitem.title}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            );
          })}
        </div>
        <div className="footer-right">
          <div className="footer-right__top">
            <ul className="social-links">
              {socialIcons.map(socialIcon => {
                const icon = socialIcon.node;
                return (
                  <li key={icon.name!}>
                    <a
                      href={icon.url!}
                      onClick={() =>
                        sendBeaconEvent({
                          name: 'clickedMarketingPageElement',
                          elementLocation: MarketingPageElementLocation.footer,
                          elementDetail: icon.name,
                          elementType: MarketingPageElementType.link
                        })
                      }
                    >
                      <LazyLoad offset={300}>
                        <img src={icon.logo!} alt={icon.name!} />
                      </LazyLoad>
                    </a>
                  </li>
                );
              })}
            </ul>
            {showLangSelect && (
              <Select
                components={{ Option: customOption }}
                options={Object.values(switcherValues)}
                className="language-switcher"
                classNamePrefix="language-switcher"
                value={switcherValues[shortLangPrefix]}
                isSearchable={false}
                menuIsOpen
                onClick={() =>
                  sendBeaconEvent({
                    name: 'clickedMarketingPageElement',
                    elementLocation: MarketingPageElementLocation.footer,
                    elementDetail: 'lang-toggle',
                    elementType: MarketingPageElementType.link
                  })
                }
              />
            )}
          </div>
          <div className="download-links padding-t-sm padding-b-sm">
            <a
              href="https://itunes.apple.com/us/app/lucidchart-flowchart-diagram-visio-viewer/id611543423?mt=8"
              onClick={() =>
                sendBeaconEvent({
                  name: 'clickedMarketingPageElement',
                  elementLocation: MarketingPageElementLocation.footer,
                  elementDetail: 'app-store',
                  elementType: MarketingPageElementType.link
                })
              }
            >
              <LazyLoad offset={300}>
                <img
                  src="https://d2slcw3kip6qmk.cloudfront.net/marketing/pages/chart/footer/app-store.svg"
                  alt="Apple App Store"
                />
              </LazyLoad>
            </a>
            <a
              href="https://play.google.com/store/apps/details?id=com.lucidchart.android.chart&amp;hl=en"
              onClick={() =>
                sendBeaconEvent({
                  name: 'clickedMarketingPageElement',
                  elementLocation: MarketingPageElementLocation.footer,
                  elementDetail: 'google-play',
                  elementType: MarketingPageElementType.link
                })
              }
            >
              <LazyLoad offset={300}>
                <img
                  src="https://d2slcw3kip6qmk.cloudfront.net/marketing/pages/chart/footer/google-play.svg"
                  alt="Google Play Store"
                />
              </LazyLoad>
            </a>
          </div>

          <div className="footer-copyright padding-t-sm">
            <Link
              to={localizePhoneNumbers(phoneLink)}
              className="contact-sales"
              beaconEvent={{
                name: 'clickedMarketingPageElement',
                elementLocation: MarketingPageElementLocation.footer,
                elementDetail: 'phone-number',
                elementType: MarketingPageElementType.link
              }}
            >
              {localizePhoneNumbers(
                i18n('mktg-footer-services-contact-sales-phone-number', {
                  service: phoneVisible
                })
              )}
            </Link>
            <p>
              <span role="img" aria-label="Copyright">
                &copy;
              </span>
              {` ${new Date().getFullYear()} Lucid Software Inc.`}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
